import React, { PropsWithChildren } from 'react';
import getDomElementProps from '../functions/getDomElementProps';

type AutoOrNums = 'auto' | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
type ColValues = 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
type FontSizeNums = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
type LineHeightNums = 1 | 2 | 3 | 4
type FloatValues = 'left' | 'right' | 'none'

export interface DivProps {
    className?: string
    cols?: ColValues
    colsLG?: ColValues
    colsMD?: ColValues
    colsSM?: ColValues
    colsXL?: ColValues
    float?: FloatValues
    floatLG?: FloatValues
    floatMD?: FloatValues
    floatSM?: FloatValues
    floatXL?: FloatValues
    fontSize?: FontSizeNums
    fontSizeLG?: FontSizeNums
    fontSizeMD?: FontSizeNums
    fontSizeSM?: FontSizeNums
    fontSizeXL?: FontSizeNums
    fontWeight?: 'normal' | 'semibold' | 'bold'
    id?: string
    isContainer?: boolean
    isRow?: boolean
    lineHeight?: LineHeightNums
    margin?: AutoOrNums
    marginBottom?: AutoOrNums
    marginBottomLG?: AutoOrNums
    marginBottomMD?: AutoOrNums
    marginBottomSM?: AutoOrNums
    marginBottomXL?: AutoOrNums
    marginLG?: AutoOrNums
    marginLeft?: AutoOrNums
    marginLeftLG?: AutoOrNums
    marginLeftMD?: AutoOrNums
    marginLeftSM?: AutoOrNums
    marginLeftXL?: AutoOrNums
    marginMD?: AutoOrNums
    marginRight?: AutoOrNums
    marginRightLG?: AutoOrNums
    marginRightMD?: AutoOrNums
    marginRightSM?: AutoOrNums
    marginRightXL?: AutoOrNums
    marginSM?: AutoOrNums
    marginTop?: AutoOrNums
    marginTopLG?: AutoOrNums
    marginTopMD?: AutoOrNums
    marginTopSM?: AutoOrNums
    marginTopXL?: AutoOrNums
    marginXL?: AutoOrNums
    marginx?: AutoOrNums
    marginxLG?: AutoOrNums
    marginxMD?: AutoOrNums
    marginxSM?: AutoOrNums
    marginxXL?: AutoOrNums
    marginy?: AutoOrNums
    marginyLG?: AutoOrNums
    marginyMD?: AutoOrNums
    marginySM?: AutoOrNums
    marginyXL?: AutoOrNums
    padding?: AutoOrNums
    paddingBottom?: AutoOrNums
    paddingBottomLG?: AutoOrNums
    paddingBottomMD?: AutoOrNums
    paddingBottomSM?: AutoOrNums
    paddingBottomXL?: AutoOrNums
    paddingLG?: AutoOrNums
    paddingLeft?: AutoOrNums
    paddingLeftLG?: AutoOrNums
    paddingLeftMD?: AutoOrNums
    paddingLeftSM?: AutoOrNums
    paddingLeftXL?: AutoOrNums
    paddingMD?: AutoOrNums
    paddingRight?: AutoOrNums
    paddingRightLG?: AutoOrNums
    paddingRightMD?: AutoOrNums
    paddingRightSM?: AutoOrNums
    paddingRightXL?: AutoOrNums
    paddingSM?: AutoOrNums
    paddingTop?: AutoOrNums
    paddingTopLG?: AutoOrNums
    paddingTopMD?: AutoOrNums
    paddingTopSM?: AutoOrNums
    paddingTopXL?: AutoOrNums
    paddingXL?: AutoOrNums
    paddingx?: AutoOrNums
    paddingxLG?: AutoOrNums
    paddingxMD?: AutoOrNums
    paddingxSM?: AutoOrNums
    paddingxXL?: AutoOrNums
    paddingy?: AutoOrNums
    paddingyLG?: AutoOrNums
    paddingyMD?: AutoOrNums
    paddingySM?: AutoOrNums
    paddingyXL?: AutoOrNums
    style?: React.CSSProperties
}

const Div = (props: PropsWithChildren<DivProps>) => {

    const classes: string[] = [];

    const addColumnClasses = () => {
        if (props.cols)
            classes.push(`ds-l-col--${props.cols}`);
        if (props.colsXL)
            classes.push(`ds-l-xl-col--${props.colsXL}`);
        if (props.colsLG)
            classes.push(`ds-l-lg-col--${props.colsLG}`);
        if (props.colsMD)
            classes.push(`ds-l-md-col--${props.colsMD}`);
        if (props.colsSM)
            classes.push(`ds-l-sm-col--${props.colsSM}`);
    };

    const addContainerClass = () => {
        if (props.isContainer)
            classes.push('ds-l-container');
    };

    const addCustomClassNames = () => {
        if (props.className)
            classes.push(props.className);
    };

    const addFloatClasses = () => {
        if (props.float)
            classes.push(`ds-u-float--${props.float}`);
        if (props.floatLG)
            classes.push(`ds-u-lg-float--${props.floatLG}`);
        if (props.floatMD)
            classes.push(`ds-u-md-float--${props.floatMD}`);
        if (props.floatSM)
            classes.push(`ds-u-sm-float--${props.floatSM}`);
        if (props.floatXL)
            classes.push(`ds-u-xl-float--${props.floatXL}`);
    };

    const addFontSizeClasses = () => {
        if (props.fontSize)
            classes.push(`ds-u-font-size--${getFontSizeName(props.fontSize)}`);
        if (props.fontSizeLG)
            classes.push(`ds-u-lg-font-size--${getFontSizeName(props.fontSizeLG)}`);
        if (props.fontSizeMD)
            classes.push(`ds-u-md-font-size--${getFontSizeName(props.fontSizeMD)}`);
        if (props.fontSizeSM)
            classes.push(`ds-u-sm-font-size--${getFontSizeName(props.fontSizeSM)}`);
        if (props.fontSizeXL)
            classes.push(`ds-u-xl-font-size--${getFontSizeName(props.fontSizeXL)}`);
    };

    const addFontWeightClasses = () => {
        if (props.fontWeight)
            classes.push(`ds-u-font-weight--${props.fontWeight}`);
    };

    const addLineHeightClass = () => {
        if (props.lineHeight)
            classes.push(`ds-u-leading--${getLineHeightName(props.lineHeight)}`);
    };

    const addMarginClasses = () => {
        if (props.margin !== undefined)
            classes.push(`ds-u-margin--${props.margin}`);
        if (props.marginBottom !== undefined)
            classes.push(`ds-u-margin-bottom--${props.marginBottom}`);
        if (props.marginBottomLG !== undefined)
            classes.push(`ds-u-lg-margin-bottom--${props.marginBottomLG}`);
        if (props.marginBottomMD !== undefined)
            classes.push(`ds-u-md-margin-bottom--${props.marginBottomMD}`);
        if (props.marginBottomSM !== undefined)
            classes.push(`ds-u-sm-margin-bottom--${props.marginBottomSM}`);
        if (props.marginBottomXL !== undefined)
            classes.push(`ds-u-xl-margin-bottom--${props.marginBottomXL}`);
        if (props.marginLG !== undefined)
            classes.push(`ds-u-lg-margin--${props.marginLG}`);
        if (props.marginLeft !== undefined)
            classes.push(`ds-u-margin-left--${props.marginLeft}`);
        if (props.marginLeftLG !== undefined)
            classes.push(`ds-u-lg-margin-left--${props.marginLeftLG}`);
        if (props.marginLeftMD !== undefined)
            classes.push(`ds-u-md-margin-left--${props.marginLeftMD}`);
        if (props.marginLeftSM !== undefined)
            classes.push(`ds-u-sm-margin-left--${props.marginLeftSM}`);
        if (props.marginLeftXL !== undefined)
            classes.push(`ds-u-xl-margin-left--${props.marginLeftXL}`);
        if (props.marginMD !== undefined)
            classes.push(`ds-u-md-margin--${props.marginMD}`);
        if (props.marginRight !== undefined)
            classes.push(`ds-u-margin-right--${props.marginRight}`);
        if (props.marginRightLG !== undefined)
            classes.push(`ds-u-lg-margin-right--${props.marginRightLG}`);
        if (props.marginRightMD !== undefined)
            classes.push(`ds-u-md-margin-right--${props.marginRightMD}`);
        if (props.marginRightSM !== undefined)
            classes.push(`ds-u-sm-margin-right--${props.marginRightSM}`);
        if (props.marginRightXL !== undefined)
            classes.push(`ds-u-xl-margin-right--${props.marginRightXL}`);
        if (props.marginSM !== undefined)
            classes.push(`ds-u-sm-margin--${props.marginSM}`);
        if (props.marginTop !== undefined)
            classes.push(`ds-u-margin-top--${props.marginTop}`);
        if (props.marginTopLG !== undefined)
            classes.push(`ds-u-lg-margin-top--${props.marginTopLG}`);
        if (props.marginTopMD !== undefined)
            classes.push(`ds-u-md-margin-top--${props.marginTopMD}`);
        if (props.marginTopSM !== undefined)
            classes.push(`ds-u-sm-margin-top--${props.marginTopSM}`);
        if (props.marginTopXL !== undefined)
            classes.push(`ds-u-xl-margin-top--${props.marginTopXL}`);
        if (props.marginXL !== undefined)
            classes.push(`ds-u-xl-margin--${props.marginXL}`);
        if (props.marginx !== undefined)
            classes.push(`ds-u-margin-x--${props.marginx}`);
        if (props.marginxLG !== undefined)
            classes.push(`ds-u-lg-margin-x--${props.marginxLG}`);
        if (props.marginxMD !== undefined)
            classes.push(`ds-u-md-margin-x--${props.marginxMD}`);
        if (props.marginxSM !== undefined)
            classes.push(`ds-u-sm-margin-x--${props.marginxSM}`);
        if (props.marginxXL !== undefined)
            classes.push(`ds-u-xl-margin-x--${props.marginxXL}`);
        if (props.marginy !== undefined)
            classes.push(`ds-u-margin-y--${props.marginy}`);
        if (props.marginyLG !== undefined)
            classes.push(`ds-u-lg-margin-y--${props.marginyLG}`);
        if (props.marginyMD !== undefined)
            classes.push(`ds-u-md-margin-y--${props.marginyMD}`);
        if (props.marginySM !== undefined)
            classes.push(`ds-u-sm-margin-y--${props.marginySM}`);
        if (props.marginyXL !== undefined)
            classes.push(`ds-u-xl-margin-y--${props.marginyXL}`);
    };

    const addPaddingClasses = () => {
        if (props.padding !== undefined)
            classes.push(`ds-u-padding--${props.padding}`);
        if (props.paddingBottom !== undefined)
            classes.push(`ds-u-padding-bottom--${props.paddingBottom}`);
        if (props.paddingBottomLG !== undefined)
            classes.push(`ds-u-lg-padding-bottom--${props.paddingBottomLG}`);
        if (props.paddingBottomMD !== undefined)
            classes.push(`ds-u-md-padding-bottom--${props.paddingBottomMD}`);
        if (props.paddingBottomSM !== undefined)
            classes.push(`ds-u-sm-padding-bottom--${props.paddingBottomSM}`);
        if (props.paddingBottomXL !== undefined)
            classes.push(`ds-u-xl-padding-bottom--${props.paddingBottomXL}`);
        if (props.paddingLG !== undefined)
            classes.push(`ds-u-lg-padding--${props.paddingLG}`);
        if (props.paddingLeft !== undefined)
            classes.push(`ds-u-padding-left--${props.paddingLeft}`);
        if (props.paddingLeftLG !== undefined)
            classes.push(`ds-u-lg-padding-left--${props.paddingLeftLG}`);
        if (props.paddingLeftMD !== undefined)
            classes.push(`ds-u-md-padding-left--${props.paddingLeftMD}`);
        if (props.paddingLeftSM !== undefined)
            classes.push(`ds-u-sm-padding-left--${props.paddingLeftSM}`);
        if (props.paddingLeftXL !== undefined)
            classes.push(`ds-u-xl-padding-left--${props.paddingLeftXL}`);
        if (props.paddingMD !== undefined)
            classes.push(`ds-u-md-padding--${props.paddingMD}`);
        if (props.paddingRight !== undefined)
            classes.push(`ds-u-padding-right--${props.paddingRight}`);
        if (props.paddingRightLG !== undefined)
            classes.push(`ds-u-lg-padding-right--${props.paddingRightLG}`);
        if (props.paddingRightMD !== undefined)
            classes.push(`ds-u-md-padding-right--${props.paddingRightMD}`);
        if (props.paddingRightSM !== undefined)
            classes.push(`ds-u-sm-padding-right--${props.paddingRightSM}`);
        if (props.paddingRightXL !== undefined)
            classes.push(`ds-u-xl-padding-right--${props.paddingRightXL}`);
        if (props.paddingSM !== undefined)
            classes.push(`ds-u-sm-padding--${props.paddingSM}`);
        if (props.paddingTop !== undefined)
            classes.push(`ds-u-padding-top--${props.paddingTop}`);
        if (props.paddingTopLG !== undefined)
            classes.push(`ds-u-lg-padding-top--${props.paddingTopLG}`);
        if (props.paddingTopMD !== undefined)
            classes.push(`ds-u-md-padding-top--${props.paddingTopMD}`);
        if (props.paddingTopSM !== undefined)
            classes.push(`ds-u-sm-padding-top--${props.paddingTopSM}`);
        if (props.paddingTopXL !== undefined)
            classes.push(`ds-u-xl-padding-top--${props.paddingTopXL}`);
        if (props.paddingXL !== undefined)
            classes.push(`ds-u-xl-padding--${props.paddingXL}`);
        if (props.paddingx !== undefined)
            classes.push(`ds-u-padding-x--${props.paddingx}`);
        if (props.paddingxLG !== undefined)
            classes.push(`ds-u-lg-padding-x--${props.paddingxLG}`);
        if (props.paddingxMD !== undefined)
            classes.push(`ds-u-md-padding-x--${props.paddingxMD}`);
        if (props.paddingxSM !== undefined)
            classes.push(`ds-u-sm-padding-x--${props.paddingxSM}`);
        if (props.paddingxXL !== undefined)
            classes.push(`ds-u-xl-padding-x--${props.paddingxXL}`);
        if (props.paddingy !== undefined)
            classes.push(`ds-u-padding-y--${props.paddingy}`);
        if (props.paddingyLG !== undefined)
            classes.push(`ds-u-lg-padding-y--${props.paddingyLG}`);
        if (props.paddingyMD !== undefined)
            classes.push(`ds-u-md-padding-y--${props.paddingyMD}`);
        if (props.paddingySM !== undefined)
            classes.push(`ds-u-sm-padding-y--${props.paddingySM}`);
        if (props.paddingyXL !== undefined)
            classes.push(`ds-u-xl-padding-y--${props.paddingyXL}`);
    };

    const addRowClass = () => {
        if (props.isRow)
            classes.push('ds-l-row');
    };

    const getClasses = () => {
        addColumnClasses();
        addContainerClass();
        addCustomClassNames();
        addFloatClasses();
        addFontSizeClasses();
        addFontWeightClasses();
        addLineHeightClass();
        addMarginClasses();
        addPaddingClasses();
        addRowClass();
        return classes.join(' ');
    };

    const getFontSizeName = (fontSizeNumber: FontSizeNums) => {
        switch (fontSizeNumber) {
            case 1:
                return 'small';
            case 2:
                return 'base';
            case 3:
                return 'lead';
            case 4:
                return 'h4';
            case 5:
                return 'h3';
            case 6:
                return 'h2';
            case 7:
                return 'h1';
            case 8:
                return 'title';
            case 9:
                return 'display';
            default:
                return 'small';
        }
    };

    const getLineHeightName = (lineHeightNumber: LineHeightNums) => {
        switch (lineHeightNumber) {
            case 1:
                return 'reset';
            case 2:
                return 'heading';
            case 3:
                return 'base';
            case 4:
                return 'lead';
            default:
                return 'reset';
        }
    }

    return <>
        <div
            {...getDomElementProps(props)}
            className={getClasses()}
        >{props.children}</div>
    </>;
}

export default Div;
